import type { Language } from '../ui/i18n';
import { CITY_SOURCES as SOURCES } from '../history/sources';
import type { DialogueReply } from './dialogue';

interface CareReply {
  readonly id: string;
  readonly label: readonly [string, string];
  readonly text: readonly [string, string];
  readonly bubble: readonly [string, string];
  readonly source: string;
}

const CARE = [
  {
    id: 'care',
    label: ['What happens at a gasthuis?', 'Wat gebeurt er in een gasthuis?'],
    text: [
      'A meal, shelter and care can make a great difference when you are ill or have nobody to help you. Look for the bread and linen in this court. Keeping a gasthuis running takes many hands!',
      'Een maaltijd, onderdak en zorg maken veel verschil als je ziek bent of niemand hebt die je helpt. Zoek het brood en linnen op deze binnenplaats. Een gasthuis draaiende houden vraagt veel handen!',
    ],
    bubble: [
      'Food, shelter and care: everyone has work here.',
      'Eten, onderdak en zorg: iedereen heeft hier werk.',
    ],
    source: SOURCES.gasthuisLife.url,
  },
  {
    id: 'staff',
    label: ['Who does all this work?', 'Wie doet al dit werk?'],
    text: [
      'Gasthuismoeders and maids look after people and the household. These are paid jobs, not only work for nuns. Some residents also pay for board and care. A gasthuis is a little community.',
      'Gasthuismoeders en meiden verzorgen de mensen en het huishouden. Dat is betaald werk, niet alleen werk voor nonnen. Sommige bewoners betalen ook voor kost en verzorging. Een gasthuis is een kleine gemeenschap.',
    ],
    bubble: [
      'Care is work for gasthuismoeders and maids.',
      'Zorg is werk voor gasthuismoeders en meiden.',
    ],
    source: SOURCES.gasthuisStaff.url,
  },
  {
    id: 'chapel',
    label: ['Why is there a chapel?', 'Waarom hoort er een kapel bij?'],
    text: [
      'Helping your neighbour is a Christian duty in this city. The gasthuis has its own chapel beside the Nes. Food, rest and prayer belong to the same daily life. Can you find its small roof among the houses?',
      'Je naaste helpen is in deze stad een christelijke plicht. Het gasthuis heeft een eigen kapel bij de Nes. Eten, rust en gebed horen bij hetzelfde dagelijkse leven. Kun je haar kleine dak tussen de huizen vinden?',
    ],
    bubble: ['Look for the gasthuis chapel on the Nes.', 'Zoek de gasthuiskapel aan de Nes.'],
    source: SOURCES.gasthuisLife.url,
  },
] as const satisfies readonly CareReply[];

const LEPROZEN: CareReply = {
  id: 'leprozen',
  label: ['Why a separate Leprozenhuis?', 'Waarom een apart Leprozenhuis?'],
  text: [
    'People with leprosy were made to live apart from the city. This Leprozenhuis is their home and a place of care. Fear and exclusion shaped their lives. Leprosy and plague are different diseases; this is not a plague ward.',
    'Mensen met lepra moesten afgezonderd van de stad wonen. Dit Leprozenhuis is hun woonplek en een plek van zorg. Angst en uitsluiting bepaalden hun leven. Lepra en pest zijn verschillende ziekten; dit is geen pestzaal.',
  ],
  bubble: [
    'The Leprozenhuis is a place of care, too.',
    'Het Leprozenhuis is ook een plek van zorg.',
  ],
  source: SOURCES.leprozen.url,
};

const VISITS: CareReply = {
  id: 'visits',
  label: ['Where are you going?', 'Waar ga je heen?'],
  text: [
    'I am on my round of house visits. My patients live in houses along the water. Watch the front doors: I go inside for a while, then walk to the next address. Can you follow the route along the canal?',
    'Ik ben op mijn ronde met huisbezoeken. Mijn patiënten wonen in huizen aan het water. Let op de voordeuren: ik ga een tijd naar binnen en loop dan naar het volgende adres. Kun je de route langs de gracht volgen?',
  ],
  bubble: [
    'My next patient is at home along the canal.',
    'Mijn volgende patiënt is thuis aan de gracht.',
  ],
  source: SOURCES.map.url,
};

export function careReplies(trade: string, language: Language): readonly DialogueReply[] | null {
  const replies =
    trade === 'physician'
      ? [VISITS, CARE[0], LEPROZEN]
      : trade === 'leprozen_carer'
        ? [LEPROZEN, CARE[0], CARE[1]]
        : trade === 'carer'
          ? CARE
          : null;
  if (!replies) return null;
  const index = language === 'nl' ? 1 : 0;
  return replies.map((reply) => ({
    id: reply.id,
    label: reply.label[index],
    text: reply.text[index],
    bubble: reply.bubble[index],
    source: reply.source,
  }));
}
